/**
 * BatchListTable Component
 *
 * User Story: US-P6-001 - Inventory Management
 * Step 3: Batch Receipt (Shipment Entry)
 *
 * Table displaying inventory batches with status indicators,
 * stock progress bars, and filtering capabilities.
 */

import { useMemo } from 'react'
import { parseDateString } from '../../utils/dateUtils'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { MoreHorizontal, Eye, Trash2, Package, AlertTriangle, Truck } from 'lucide-react'
import BatchStatusBadge from './BatchStatusBadge'

/**
 * Format a date for display
 */
function formatDate(dateString) {
  if (!dateString) return '-'
  return parseDateString(dateString).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

/**
 * Format a number as currency (GBP)
 */
function formatCurrency(value) {
  if (value === null || value === undefined) return '-'
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

/**
 * Format a number with appropriate precision
 */
function formatNumber(value, decimals = 2) {
  if (value === null || value === undefined) return '-'
  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  }).format(value)
}

/**
 * Calculate percentage remaining
 */
function calculatePercentage(remaining, received) {
  if (!received || received === 0) return 0
  return Math.round((remaining / received) * 100)
}

/**
 * BatchListTable Component
 *
 * @param {Object} props
 * @param {Array} props.batches - Array of batch objects
 * @param {boolean} props.isLoading - Whether data is loading
 * @param {Function} props.onRowClick - Callback when a row is clicked
 * @param {Function} props.onDelete - Callback when delete is clicked
 * @param {string} props.searchQuery - Search query for filtering
 */
export default function BatchListTable({
  batches = [],
  isLoading = false,
  onRowClick,
  onDelete,
  searchQuery = '',
}) {
  // Filter batches based on search query
  const filteredBatches = useMemo(() => {
    if (!searchQuery.trim()) return batches

    const query = searchQuery.toLowerCase()
    return batches.filter(batch => {
      const batchCode = batch.batch_code?.toLowerCase() || ''
      const itemName = batch.item?.name?.toLowerCase() || ''
      const supplierName = batch.supplier?.name?.toLowerCase() || ''
      const invoiceRef = batch.invoice_reference?.toLowerCase() || ''

      return (
        batchCode.includes(query) ||
        itemName.includes(query) ||
        supplierName.includes(query) ||
        invoiceRef.includes(query)
      )
    })
  }, [batches, searchQuery])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="text-muted-foreground">Loading batches...</div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {filteredBatches.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-48 text-center">
          <Package className="h-12 w-12 text-muted-foreground/50 mb-3" />
          <p className="text-muted-foreground">
            {searchQuery ? 'No batches match your search' : 'No batches recorded yet'}
          </p>
          {!searchQuery && (
            <p className="text-sm text-muted-foreground mt-1">
              Record a shipment to create the first batch
            </p>
          )}
        </div>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Batch Code</TableHead>
                <TableHead>Item</TableHead>
                <TableHead>Supplier</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Qty Received</TableHead>
                <TableHead className="text-right">Remaining</TableHead>
                <TableHead className="text-right">Unit Cost</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredBatches.map((batch) => {
                const percentage = calculatePercentage(
                  batch.quantity_remaining,
                  batch.quantity_received
                )
                const unit = batch.item?.unit_of_measure || ''
                const canDelete = batch.quantity_received === batch.quantity_remaining

                return (
                  <TableRow
                    key={batch.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => onRowClick?.(batch)}
                  >
                    <TableCell className="font-mono text-sm">
                      {batch.batch_code}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">{batch.item?.name || '-'}</span>
                        <span className="text-xs text-muted-foreground">
                          {batch.item?.category?.replace('_', ' ')}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {batch.supplier?.name || '-'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(batch.purchase_date)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatNumber(batch.quantity_received)} {unit}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="space-y-1">
                        <div className="flex items-center justify-end gap-2">
                          <span>{formatNumber(batch.quantity_remaining)} {unit}</span>
                          <span className="text-xs text-muted-foreground">
                            ({percentage}%)
                          </span>
                        </div>
                        <Progress
                          value={percentage}
                          className="h-1.5 w-16 ml-auto"
                        />
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-help border-b border-dotted border-muted-foreground/50">
                              {formatCurrency(batch.unit_cost)}/{unit}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="left" className="text-xs">
                            <div className="space-y-1">
                              <div className="flex justify-between gap-4">
                                <span className="text-muted-foreground">Product:</span>
                                <span>{formatCurrency(batch.product_cost)}</span>
                              </div>
                              {batch.delivery_cost > 0 && (
                                <div className="flex justify-between gap-4">
                                  <span className="text-muted-foreground flex items-center gap-1">
                                    <Truck className="h-3 w-3" /> Delivery:
                                  </span>
                                  <span>{formatCurrency(batch.delivery_cost)}</span>
                                </div>
                              )}
                              <div className="flex justify-between gap-4 pt-1 border-t">
                                <span className="font-medium">Landed:</span>
                                <span className="font-medium">{formatCurrency(batch.total_cost)}</span>
                              </div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell>
                      <BatchStatusBadge status={batch.status} size="sm" />
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => onRowClick?.(batch)}>
                            <Eye className="mr-2 h-4 w-4" />
                            View Details
                          </DropdownMenuItem>
                          {onDelete && canDelete && (
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() => onDelete(batch)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          )}
                          {onDelete && !canDelete && (
                            <DropdownMenuItem disabled className="text-muted-foreground">
                              <AlertTriangle className="mr-2 h-4 w-4" />
                              Cannot delete (in use)
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
